import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useFetch } from '../../../hooks/useFetch';
import { API_URLS } from '../../../constants/api';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const AnalyticsModal = ({ isOpen, onClose, person, type, filters }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCharges, setExpandedCharges] = useState(new Set());

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalTop = window.getComputedStyle(document.body).top;
      const scrollY = window.scrollY;

      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      const preventTouchMove = (e) => {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', preventTouchMove, {
        passive: false
      });
      document.addEventListener('touchstart', preventTouchMove, {
        passive: false
      });

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('touchstart', preventTouchMove);
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Fetch analytics data
  const nameParam = encodeURIComponent(
    `${person.firstName}|${person.lastName}`
  );
  const endpoint =
    type === 'judge'
      ? `${API_URLS.ANALYTICS_JUDGE}/${nameParam}`
      : `${API_URLS.ANALYTICS_PROCECUTOR}/${nameParam}`;

  const {
    data: analytics,
    isLoading,
    error
  } = useFetch(endpoint, {
    jurisdiction: filters.jurisdiction,
    courthouse: filters.courthouse
  });

  // Debug logging
  useEffect(() => {
    if (analytics) {
      console.log('Analytics data received:', analytics);
      console.log('Overall stats:', analytics.overallStats);
      console.log('Baseline comparison:', analytics.baselineComparison);
      console.log('Charge specific stats:', analytics.chargeSpecificStats);
      console.log('Time series data:', analytics.timeSeriesData);
    }
    if (error) {
      console.error('Analytics error:', error);
    }
  }, [analytics, error]);

  const toggleChargeExpansion = (chargeCode) => {
    const newExpanded = new Set(expandedCharges);
    if (newExpanded.has(chargeCode)) {
      newExpanded.delete(chargeCode);
    } else {
      newExpanded.add(chargeCode);
    }
    setExpandedCharges(newExpanded);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatMonths = (months) => {
    if (!months) return 'N/A';
    return `${months.toFixed(1)} months`;
  };

  const getComparisonIcon = (value, baseline) => {
    if (
      value === null ||
      value === undefined ||
      baseline === null ||
      baseline === undefined
    )
      return <Minus className="w-4 h-4 text-gray-400" />;
    if (value > baseline)
      return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (value < baseline)
      return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getOrderedSentencingData = (sentencingDistribution) => {
    const order = [
      'Dismissed',
      '0 days',
      '1-6 months',
      '6-12 months',
      '12-24 months',
      '24+ months'
    ];

    return order
      .filter((category) => sentencingDistribution[category] !== undefined)
      .map((category) => ({
        category,
        count: sentencingDistribution[category]
      }));
  };

  const renderOverview = () => {
    if (!analytics) return null;

    const { overallStats, baselineComparison } = analytics;

    // Check if overallStats exists - but only show error if we're not loading
    if (!overallStats) {
      // If we're still loading, don't show the error message
      if (isLoading) return null;

      return (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No analytics data available
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Unable to load statistics for this {type}. Please try again
                    later.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const courthouseBaseline = baselineComparison?.courthouseBaseline;

    // If no baseline data, show only the person's stats without comparisons
    if (!courthouseBaseline) {
      return (
        <div className="space-y-6">
          {/* Overview Header */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Overview - All Charges
            </h3>
            <p className="text-sm text-blue-700">
              Statistics based on all cases handled by this {type} over the last
              5 years
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Cases</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {overallStats.totalCases}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Avg Sentence</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatMonths(overallStats.averageSentenceMonths)}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Diversion Rate</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatPercentage(overallStats.diversionRate)}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Dismissal Rate</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatPercentage(overallStats.dismissalRate)}
              </div>
            </div>
          </div>

          {/* Sentencing Distribution Chart */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">
              Sentencing Distribution
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={getOrderedSentencingData(
                    overallStats.sentencingDistribution || {}
                  )}
                >
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Overview Header */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-blue-900 mb-2">
            Overview - All Charges
          </h3>
          <p className="text-sm text-blue-700">
            Statistics based on all cases handled by this {type} over the last 5
            years
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Cases</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {overallStats.totalCases}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Sentence</span>
              {getComparisonIcon(
                overallStats.averageSentenceMonths,
                courthouseBaseline.averageSentenceMonths
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatMonths(overallStats.averageSentenceMonths)}
            </div>
            <div className="text-xs text-gray-500">
              vs {formatMonths(courthouseBaseline.averageSentenceMonths)}{' '}
              courthouse avg
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Diversion Rate</span>
              {getComparisonIcon(
                overallStats.diversionRate,
                courthouseBaseline.diversionRate
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatPercentage(overallStats.diversionRate)}
            </div>
            <div className="text-xs text-gray-500">
              vs {formatPercentage(courthouseBaseline.diversionRate)} courthouse
              avg
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Dismissal Rate</span>
              {getComparisonIcon(
                overallStats.dismissalRate,
                courthouseBaseline.dismissalRate
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatPercentage(overallStats.dismissalRate)}
            </div>
            <div className="text-xs text-gray-500">
              vs {formatPercentage(courthouseBaseline.dismissalRate)} courthouse
              avg
            </div>
          </div>
        </div>

        {/* Sentencing Distribution Chart */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">
            Sentencing Distribution
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getOrderedSentencingData(
                  overallStats.sentencingDistribution
                )}
              >
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderChargeBreakdown = () => {
    if (
      !analytics?.chargeSpecificStats ||
      analytics.chargeSpecificStats.length === 0
    ) {
      return (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-center text-gray-500">
              <p>No charge-specific data available</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {analytics.chargeSpecificStats
          .sort((a, b) => a.chargeCode.localeCompare(b.chargeCode))
          .map((charge) => (
            <div
              key={charge.chargeCode}
              className="border border-gray-200 rounded-lg"
            >
              <button
                onClick={() => toggleChargeExpansion(charge.chargeCode)}
                className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">
                      {charge.chargeCode}
                    </div>
                    <div className="text-sm text-gray-600">
                      {charge.chargeDescription}
                    </div>
                    <div className="text-sm text-gray-500">
                      {charge.caseCount} cases
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">
                      {formatMonths(charge.averageSentenceMonths)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatPercentage(charge.diversionRate)} diversion
                    </div>
                  </div>
                </div>
              </button>

              {expandedCharges.has(charge.chargeCode) && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  <div className="pt-4 space-y-3">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">
                        Sentencing Distribution
                      </h5>
                      <div className="h-48 mb-3">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={getOrderedSentencingData(
                              charge.sentencingDistribution
                            )}
                          >
                            <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(charge.sentencingDistribution).map(
                          ([category, count]) => (
                            <div
                              key={category}
                              className="flex justify-between"
                            >
                              <span className="text-gray-600">{category}:</span>
                              <span className="font-medium">{count} cases</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Dismissal Rate:</span>
                        <span className="font-medium ml-2">
                          {formatPercentage(charge.dismissalRate)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Plea Rate:</span>
                        <span className="font-medium ml-2">
                          {formatPercentage(charge.pleaRate)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Trial Rate:</span>
                        <span className="font-medium ml-2">
                          {formatPercentage(charge.trialRate)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    );
  };

  const renderTimeTrends = () => {
    if (!analytics?.timeSeriesData || analytics.timeSeriesData.length === 0) {
      return (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-center text-gray-500">
              <p>No time series data available</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">
            Average Sentence Over Time
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.timeSeriesData}>
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="averageSentenceMonths"
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">
            Diversion Rate Over Time
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.timeSeriesData}>
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="diversionRate"
                  stroke="#10b981"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Handle clicking outside modal to close
  const handleBackdropClick = (e) => {
    // Close modal when clicking on the backdrop (outer div)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks on the modal content from closing the modal
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
      onClick={handleBackdropClick}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-full max-h-screen overflow-hidden flex flex-col modal-content"
        data-modal-content
        style={{ maxHeight: '90vh' }}
        onClick={handleModalClick}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => {
          e.stopPropagation();
          const modalContent = e.currentTarget;
          const isScrollingModal =
            modalContent.scrollHeight > modalContent.clientHeight;
          if (!isScrollingModal) {
            e.preventDefault();
          }
        }}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {type === 'judge' ? 'Judge' : 'Prosecutor'} Analytics
                </h2>
                <p className="text-sm text-gray-500">
                  {person.firstName} {person.lastName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'charges', label: 'Charge Breakdown' },
              { id: 'trends', label: 'Time Trends' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto flex-1"
          style={{ maxHeight: 'calc(90vh - 140px)' }}
        >
          {isLoading && (
            <div className="space-y-6">
              {/* Loading header with spinner */}
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 font-medium">
                    Loading analytics...
                  </span>
                </div>
              </div>

              {/* Placeholder content that mimics the actual layout */}
              <div className="animate-pulse space-y-6">
                {/* Header placeholder */}
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>

                {/* Key metrics placeholder - 2x2 grid */}
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-gray-50 p-4 rounded-lg">
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-3/4 mb-1"></div>
                      <div className="h-2 bg-gray-200 rounded w-full"></div>
                    </div>
                  ))}
                </div>

                {/* Chart placeholder */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                  <div className="h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                    <div className="text-gray-400 text-sm">
                      Loading chart...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="text-red-600">Error loading analytics</div>
              <div className="text-sm text-gray-500 mt-2">{error.message}</div>
            </div>
          )}

          {!isLoading && !error && !analytics && (
            <div className="text-center py-12">
              <div className="text-gray-600">No analytics data available</div>
              <div className="text-sm text-gray-500 mt-2">
                No cases found for this {type}. Try selecting a different {type}{' '}
                or adjusting your search filters.
              </div>
            </div>
          )}

          {!isLoading && !error && analytics && (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'charges' && renderChargeBreakdown()}
              {activeTab === 'trends' && renderTimeTrends()}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

AnalyticsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  person: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['judge', 'prosecutor']).isRequired,
  filters: PropTypes.object.isRequired
};

export default AnalyticsModal;
